/**
 * MobileNav — Bottom-Navigation für Mobilgeräte (App-Style)
 *
 * DESIGN: Wie die besten Apps (Todoist, Microsoft To Do, Asana)
 * - 4 Tabs (Dashboard, Akten, Kalender, Mehr) + zentraler, erhöhter KI-Assistent-Button
 * - Der Assistent-Button ist IMMER sichtbar, mittig, leicht erhöht über der Leiste
 * - "Mehr" öffnet ein Bottom-Sheet mit allen weiteren Modulen (inkl. Aufgaben)
 * - Touch-Targets: min 44px
 * - Safe-Area-Bottom für iPhone/Android
 * - Aktiver Tab mit Koop-Rot Akzent
 */

import { useLocation } from 'wouter';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useState } from 'react';
import {
  LayoutDashboard,
  FolderOpen,
  CheckSquare,
  Calendar,
  MoreHorizontal,
  MessageSquare,
  Users,
  Receipt,
  Settings,
  Mail,
  Target,
  Megaphone,
  ChevronRight,
  LogOut,
  Files,
  Bot,
  Sparkles,
} from 'lucide-react';
import BottomSheet from './BottomSheet';
import AgentenKommandozentrale from './AgentenKommandozentrale';
import AssistantSheet from './AssistantSheet';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { id: 'akten', label: 'Akten', icon: FolderOpen, path: '/akten/gutachten' },
  { id: 'kalender', label: 'Kalender', icon: Calendar, path: '/kalender' },
  { id: 'mehr', label: 'Mehr', icon: MoreHorizontal, path: '' },
];

const MORE_ITEMS = [
  { id: 'aufgaben', label: 'Aufgaben', icon: CheckSquare, path: '/aufgaben', desc: 'Offene Aufgaben & To-dos' },
  { id: 'dokumente', label: 'Dokumente', icon: Files, path: '/dokumente', desc: 'Dateien, Ordner & Vorschau' },
  { id: 'kommunikation', label: 'Kommunikation', icon: MessageSquare, path: '/kommunikation', desc: 'Chat, E-Mail & Nachrichten' },
  { id: 'crm', label: 'CRM & Kontakte', icon: Users, path: '/crm', desc: 'Kunden, Partner & Kontakte' },
  { id: 'marketing', label: 'Marketing', icon: Megaphone, path: '/marketing', desc: 'Kampagnen & Leads' },
  { id: 'finanzen', label: 'Finanzen', icon: Receipt, path: '/buchhaltung', desc: 'Rechnungen & Buchhaltung' },
  { id: 'einstellungen', label: 'Einstellungen', icon: Settings, path: '/einstellungen', desc: 'Profil, Team & System' },
];

export default function MobileNav() {
  const [location, setLocation] = useLocation();
  const { closeAkte } = useApp();
  const { profile, signOut } = useAuth();
  const [showMore, setShowMore] = useState(false);
  const [showKommandozentrale, setShowKommandozentrale] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const isAdmin = profile?.rolle === 'admin';

  const isActive = (path: string) => {
    if (path === '/') return location === '/' || location === '/dashboard';
    if (path === '') return false;
    return location.startsWith(path);
  };

  // Prüfe ob einer der "Mehr"-Items aktiv ist
  const isMoreActive = MORE_ITEMS.some(item => location.startsWith(item.path));

  return (
    <>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 safe-area-bottom">
        <div className="flex items-stretch justify-around h-16">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = item.id === 'mehr' ? (isMoreActive || showMore) : isActive(item.path);
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'mehr') {
                    setShowMore(true);
                  } else {
                    closeAkte();
                    setLocation(item.path);
                    setShowMore(false);
                  }
                }}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-[64px] min-h-[44px] transition-colors relative ${
                  active ? 'text-[#C41E3A]' : 'text-gray-400'
                }`}
              >
                {/* Active indicator line */}
                {active && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#C41E3A] rounded-full" />
                )}
                <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
                <span className={`text-[10px] leading-tight ${active ? 'font-bold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Zentraler KI-Assistent-Button — immer sichtbar, mittig, leicht erhöht */}
      <button
        onClick={() => setShowAssistant(true)}
        aria-label="KI-Assistent öffnen"
        className="md:hidden fixed bottom-2.5 left-1/2 -translate-x-1/2 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] border-4 border-white shadow-xl shadow-[#6d8bff]/50 flex items-center justify-center text-white active:scale-95 transition-transform"
      >
        <span className="absolute inset-0 rounded-full ring-2 ring-[#9d6bff]/40 animate-ping" />
        <Sparkles className="w-6 h-6 relative" />
      </button>

      {/* Mehr Bottom Sheet */}
      <BottomSheet
        open={showMore}
        onClose={() => setShowMore(false)}
        title="Weitere Module"
        height="auto"
      >
        <div className="py-2">
          {/* Kommandozentrale — nur Admin, ganz oben */}
          {isAdmin && (
            <>
              <button
                onClick={() => {
                  setShowMore(false);
                  setTimeout(() => setShowKommandozentrale(true), 200);
                }}
                className="w-full flex items-center gap-4 px-5 py-3.5 transition-colors min-h-[56px] hover:bg-gray-50 active:bg-gray-100"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#C41E3A]/10">
                  <Bot className="w-5 h-5 text-[#C41E3A]" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-semibold text-[#9B1C1C]">Kommandozentrale</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Vorschläge, Aufgaben & Protokoll</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="w-2 h-2 bg-[#C41E3A] rounded-full animate-pulse" />
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </button>
              <div className="h-px bg-gray-100 my-1 mx-5" />
            </>
          )}
          {MORE_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = location.startsWith(item.path);
            return (
              <button
                key={item.id}
                onClick={() => {
                  closeAkte();
                  setLocation(item.path);
                  setShowMore(false);
                }}
                className={`w-full flex items-center gap-4 px-5 py-3.5 transition-colors min-h-[56px] ${
                  active ? 'bg-red-50' : 'hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  active ? 'bg-[#C41E3A]/10' : 'bg-gray-100'
                }`}>
                  <Icon className={`w-5 h-5 ${active ? 'text-[#C41E3A]' : 'text-gray-500'}`} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className={`text-sm font-semibold ${active ? 'text-[#C41E3A]' : 'text-gray-800'}`}>
                    {item.label}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{item.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </button>
            );
          })}

          {/* Divider */}
          <div className="h-px bg-gray-100 my-2 mx-5" />

          {/* User Info + Logout */}
          <div className="px-5 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C41E3A] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
              {profile?.initialen || 'AK'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{profile?.name || 'Benutzer'}</p>
              <p className="text-[11px] text-gray-400">
                {profile?.rolle === 'admin' ? 'Administrator' : profile?.rolle === 'sachbearbeiter' ? 'Sachbearbeiter' : 'Mitarbeiter'}
              </p>
            </div>
            <button
              onClick={async () => {
                setShowMore(false);
                await signOut();
                setLocation('/login');
              }}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
            >
              <LogOut className="w-4.5 h-4.5 text-gray-400" />
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* KI-Assistent als Fullscreen-Sheet */}
      <AssistantSheet open={showAssistant} onClose={() => setShowAssistant(false)} />

      {/* Kommandozentrale als Fullscreen-Sheet */}
      {isAdmin && (
        <AgentenKommandozentrale open={showKommandozentrale} onOpenChange={setShowKommandozentrale} />
      )}
    </>
  );
}
